import { Dropdown, SelectOption } from "components/dropdown/Dropdown";
import { useField, useFormikContext } from "formik";
import { options } from "yargs";
import {
  ParsedWorkflowItem,
  ReferenceData,
  WorkflowBuilderErrors,
  WorkflowReferenceDataNames,
} from "../WorkflowBuilderPage";
import { SetStateAction, useEffect, useState } from "react";
import { MultiDropdown } from "components/dropdown/MultiDropdown";
import _ from "lodash";
import {
  changeWorkflowBuilderReferenceData,
  updateWorkflowBuilderValue,
} from "./WorkflowItem";
import { EditNode } from "./DocumentUploadTrigger";

interface Props {
  fieldName: string;
  documentOptions: SelectOption[];
  projectOptions: SelectOption[];
  type: "trigger" | "action";
  triggerData: any;
  parsedData: ParsedWorkflowItem | null;
  setParsedData: React.Dispatch<SetStateAction<ParsedWorkflowItem | null>>;
  referenceData: ReferenceData | null;
  setReferenceData: React.Dispatch<SetStateAction<ReferenceData | null>>;
  contactTypes: SelectOption[];
  userPools: SelectOption[];
  showTriggerTypeSelector: boolean;
  setShowTriggerTypeSelector: React.Dispatch<SetStateAction<boolean>>;
  workflowBuilderErrors: WorkflowBuilderErrors;
}

const DocumentRequestTrigger: React.FC<Props> = ({
  fieldName,
  documentOptions,
  projectOptions,
  type,
  triggerData,
  parsedData,
  setParsedData,
  referenceData,
  setReferenceData,
  contactTypes,
  userPools,
  showTriggerTypeSelector,
  setShowTriggerTypeSelector,
  workflowBuilderErrors,
}) => {
  const [relevantErrors, setRelevantErrors] = useState<string[] | null>(null);

  useEffect(() => {
    if (
      workflowBuilderErrors &&
      workflowBuilderErrors.trigger &&
      workflowBuilderErrors.trigger.hasOwnProperty(0)
    ) {
      const tempErrors = workflowBuilderErrors.trigger[0];

      if (Array.isArray(tempErrors) && tempErrors.length > 0) {
        setRelevantErrors(tempErrors);
      } else {
        setRelevantErrors(null);
      }
    } else {
      setRelevantErrors(null);
    }
  }, [workflowBuilderErrors]);

  const handleChangeDocument = (option: SelectOption) => {
    if (parsedData) {
      let newParsedData: any = {
        ...parsedData,
      };

      const newValue = option ? option.value : null;

      newParsedData.documentTypesID = newValue;

      setParsedData(newParsedData);

      let newReferenceData = _.cloneDeep(referenceData);

      if (newReferenceData?.trigger) {
        newReferenceData.trigger.documentTypesID = newValue;
      }

      setReferenceData(newReferenceData);
    }
  };

  const handleBooleanChange = (fieldName: string) => {
    if (parsedData) {
      let newParsedData: any = {
        ...parsedData,
      };

      const newValue =
        newParsedData && newParsedData.hasOwnProperty(fieldName)
          ? !newParsedData[fieldName]
          : newParsedData && !newParsedData.hasOwnProperty(fieldName)
          ? true
          : false;

      newParsedData[fieldName] = newValue;

      setParsedData(newParsedData);

      let newReferenceData = _.cloneDeep(referenceData);

      if (newReferenceData?.trigger) {
        newReferenceData.trigger[fieldName] = newValue;
      }

      setReferenceData(newReferenceData);
    }
  };

  const handleSave = () => {};

  const handleDropdownChangeMulti = (
    options: SelectOption[],
    fieldName: string
  ) => {
    const optionIds = options ? options.map(item => item.value) : null;

    const newParsedData = updateWorkflowBuilderValue(
      parsedData,
      "0",
      fieldName,
      optionIds,
      "replace"
    );

    setParsedData(newParsedData);

    let newReferenceData = _.cloneDeep(referenceData);

    if (newReferenceData?.trigger) {
      newReferenceData.trigger[fieldName] = optionIds;
    }

    setReferenceData(newReferenceData);
  };

  const handleDropdownChangeSingle = (
    option: SelectOption,
    fieldName: string
  ) => {
    const optionId = option ? option.value : null;

    const newParsedData = updateWorkflowBuilderValue(
      parsedData,
      "0",
      fieldName,
      optionId,
      "replace"
    );

    setParsedData(newParsedData);

    let newReferenceData = _.cloneDeep(referenceData);

    if (newReferenceData?.trigger) {
      newReferenceData.trigger[fieldName] = optionId;
    }

    setReferenceData(newReferenceData);
  };

  const title = "Document request";
  return (
    <div
      className={`DocumentUploadTrigger TriggerContainer DetailsItemContent ${
        relevantErrors && relevantErrors.length > 0 ? "Error" : ""
      }`}
    >
      <label className="Title">
        <div className="Text">{`Trigger - ${title}`}</div>

        <EditNode setShowSelector={setShowTriggerTypeSelector} />
      </label>

      <div className="Row">
        <div className="Label">Document type</div>

        <Dropdown
          onChange={handleChangeDocument}
          value={
            triggerData && triggerData.documentTypesID
              ? documentOptions.find(
                  o => o.value === triggerData.documentTypesID
                )
              : null
          }
          options={documentOptions}
          placeholder="Please select"
          roundedRectangle
          portalMode={true}
          isClearable={true}
        />
      </div>

      <div className="Row">
        <div className="Label">
          Contact types <span className="Italic"> - opt</span>
        </div>

        <MultiDropdown
          customClassName="MultiDropdown"
          onChange={(e: any) => handleDropdownChangeMulti(e, "contactTypesIDs")}
          value={
            contactTypes &&
            Array.isArray(contactTypes) &&
            triggerData &&
            triggerData.contactTypesIDs &&
            Array.isArray(triggerData.contactTypesIDs)
              ? contactTypes.filter(o =>
                  triggerData.contactTypesIDs.includes(o.value)
                )
              : null
          }
          options={contactTypes}
          placeholder="Please select"
          isClearable={false}
          roundedRectangle
          portalMode={true}
        />
      </div>

      {/* Note: should be 2 fields, multi select contact types and single select user pool, validation requires either one or more contact types or one user pool: */}

      <div className="Row">
        <div className="Label">Userpool</div>

        <Dropdown
          onChange={(e: any) => handleDropdownChangeSingle(e, "userPoolsID")}
          value={
            userPools &&
            Array.isArray(userPools) &&
            triggerData &&
            triggerData.userPoolsID &&
            typeof triggerData.userPoolsID === "number" &&
            userPools.filter(item => triggerData.userPoolsID === item.value)
              .length === 1
              ? userPools.filter(
                  item => triggerData.userPoolsID === item.value
                )[0]
              : null
          }
          options={userPools}
          roundedRectangle
          isClearable={true}
        />
      </div>

      {/* NOTE: Need message text flag and reason code flag: */}
      <div className="Row">
        <div className="Label">Message text</div>

        <div
          className="Radio"
          role="radio"
          aria-checked={triggerData && triggerData.bMessageText}
        >
          <input
            id={"bMessageTextCheckbox"}
            type="radio"
            name={"bMessageTextCheckbox"}
            value="bMessageText"
            onClick={() => handleBooleanChange("bMessageText")}
            checked={triggerData && triggerData.bMessageText}
          />
          <label
            className="CustomInput"
            htmlFor={"bMessageTextCheckbox"}
          ></label>
        </div>
      </div>

      <div className="Row">
        <div className="Label">Reason code</div>

        <div
          className="Radio"
          role="radio"
          aria-checked={triggerData && triggerData.bReasonCode}
        >
          <input
            id={"bReasonCodeCheckbox"}
            type="radio"
            name={"bReasonCodeCheckbox"}
            value="bReasonCode"
            onClick={() => handleBooleanChange("bReasonCode")}
            checked={triggerData && triggerData.bReasonCode}
          />
          <label
            className="CustomInput"
            htmlFor={"bReasonCodeCheckbox"}
          ></label>
        </div>
      </div>

      {relevantErrors && relevantErrors.length > 0 && (
        <div className="Errors">
          {relevantErrors.map(error => {
            return <div className="Error">{error}</div>;
          })}
        </div>
      )}
      {/* <button type="button" className="SaveBtn" onClick={handleSave}>
        Save
      </button> */}
    </div>
  );
};

export default DocumentRequestTrigger;
